import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Building2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
const STATUS_LABELS = {
  active: 'Actief', trialing: 'Trial', past_due: 'Betaling verlopen',
  canceled: 'Geannuleerd', expired: 'Verlopen',
};

const SuperAdminPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [planDraft, setPlanDraft] = useState({ plan: 'starter', subscription_status: 'active' });
  const [whitelistReason, setWhitelistReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [mailLog, setMailLog] = useState([]);
  const [mailLogLoading, setMailLogLoading] = useState(true);
  const [authLog, setAuthLog] = useState([]);
  const [authLogLoading, setAuthLogLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '', reply_to: '' });
  const [sendingMail, setSendingMail] = useState(false);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon organisaties niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMailLog = useCallback(async () => {
    setMailLogLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/mail/log`);
      setMailLog(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon verzendlog niet laden');
    } finally {
      setMailLogLoading(false);
    }
  }, []);

  const loadAuthLog = useCallback(async () => {
    setAuthLogLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/auth/log`);
      setAuthLog(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon authenticatielog niet laden');
    } finally {
      setAuthLogLoading(false);
    }
  }, []);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);
  useEffect(() => { loadMailLog(); }, [loadMailLog]);
  useEffect(() => { loadAuthLog(); }, [loadAuthLog]);

  const sendMail = async (e) => {
    e.preventDefault();
    setSendingMail(true);
    try {
      await axios.post(`${API}/superadmin/mail/send`, {
        to: composeForm.to,
        subject: composeForm.subject,
        body: composeForm.body,
        reply_to: composeForm.reply_to || null,
      });
      toast.success('Mail verzonden');
      setComposeOpen(false);
      setComposeForm({ to: '', subject: '', body: '', reply_to: '' });
      await loadMailLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verzenden mislukt');
    } finally {
      setSendingMail(false);
    }
  };

  const openDetail = async (orgId) => {
    setSelectedOrgId(orgId);
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/organizations/${orgId}`);
      setDetail(response.data);
      setPlanDraft({
        plan: response.data.organization.plan || 'starter',
        subscription_status: response.data.organization.subscription_status || 'active',
      });
      setWhitelistReason(response.data.organization.whitelist_reason || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon organisatie niet laden');
    } finally {
      setDetailLoading(false);
    }
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/organizations/${selectedOrgId}/plan`, planDraft);
      toast.success('Plan bijgewerkt');
      await Promise.all([loadOrganizations(), openDetail(selectedOrgId)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bijwerken mislukt');
    } finally {
      setSaving(false);
    }
  };

  const toggleWhitelist = async (nextValue) => {
    if (nextValue && !whitelistReason.trim()) {
      toast.error('Vul een reden in voordat je whitelist aanzet');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/organizations/${selectedOrgId}/whitelist`, {
        is_whitelisted: nextValue,
        reason: nextValue ? whitelistReason.trim() : null,
      });
      toast.success(nextValue ? 'Organisatie is gewhitelist' : 'Whitelist uitgezet');
      await Promise.all([loadOrganizations(), openDetail(selectedOrgId)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bijwerken mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Platform-beheer</h1>
            <p className="text-slate-500 text-sm">Organisaties, abonnementen en whitelist — alleen zichtbaar voor platform-admins.</p>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4" /> Mail versturen
            </CardTitle>
            <Button size="sm" onClick={() => setComposeOpen(true)}>Nieuwe mail</Button>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 text-sm mb-4">
              Verstuur een e-mail als info@zoea.nl — handig voor partners of samenwerkingen.
            </p>
            {mailLogLoading ? (
              <p className="text-slate-500 text-sm">Laden...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aan</TableHead>
                    <TableHead>Onderwerp</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verzonden op</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mailLog.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-slate-400">Nog geen mails verzonden.</TableCell></TableRow>
                  )}
                  {mailLog.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.to}</TableCell>
                      <TableCell>{entry.subject}</TableCell>
                      <TableCell>
                        <Badge className={entry.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {entry.status === 'sent' ? 'Verzonden' : 'Mislukt'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(entry.created_at).toLocaleString('nl-NL')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4" /> Authenticatielog
            </CardTitle>
          </CardHeader>
          <CardContent>
            {authLogLoading ? <p className="text-slate-500 text-sm">Laden...</p> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Tijd</TableHead><TableHead>E-mail</TableHead><TableHead>Uitkomst</TableHead><TableHead>Reden</TableHead><TableHead>IP</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {authLog.length === 0 && <TableRow><TableCell colSpan={5} className="text-slate-400">Nog geen authenticatiegebeurtenissen.</TableCell></TableRow>}
                    {authLog.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap">{new Date(entry.created_at).toLocaleString('nl-NL')}</TableCell>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell><Badge className={entry.outcome === 'success' ? 'bg-emerald-100 text-emerald-700' : entry.outcome === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>{entry.outcome === 'success' ? 'Geslaagd' : entry.outcome === 'failed' ? 'Mislukt' : 'Info'}</Badge></TableCell>
                        <TableCell>{entry.reason}</TableCell>
                        <TableCell>{entry.ip_address || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-4 h-4" /> Organisaties ({organizations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-500 text-sm">Laden...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Naam</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Toegang</TableHead>
                    <TableHead>Whitelist</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org) => (
                    <OrganizationRow key={org.id} org={org} onManage={openDetail} />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedOrgId} onOpenChange={(open) => { if (!open) { setSelectedOrgId(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.organization?.name || 'Organisatie'}</DialogTitle>
          </DialogHeader>

          {detailLoading || !detail ? (
            <p className="text-slate-500 text-sm">Laden...</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Plan</label>
                  <Select value={planDraft.plan} onValueChange={(v) => setPlanDraft((d) => ({ ...d, plan: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Abonnement-status</label>
                  <Select
                    value={planDraft.subscription_status}
                    onValueChange={(v) => setPlanDraft((d) => ({ ...d, subscription_status: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Actief</SelectItem>
                      <SelectItem value="trialing">Trial</SelectItem>
                      <SelectItem value="past_due">Betaling verlopen</SelectItem>
                      <SelectItem value="canceled">Geannuleerd</SelectItem>
                      <SelectItem value="expired">Verlopen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button size="sm" onClick={savePlan} disabled={saving}>Plan opslaan</Button>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Whitelist (gratis volledige toegang)</p>
                    <p className="text-xs text-slate-500">Overrulet abonnement/betaalstatus volledig.</p>
                  </div>
                  <Switch
                    checked={!!detail.organization.is_whitelisted}
                    disabled={saving}
                    onCheckedChange={toggleWhitelist}
                  />
                </div>
                <Textarea
                  placeholder="Reden (verplicht om whitelist aan te zetten)"
                  value={whitelistReason}
                  onChange={(e) => setWhitelistReason(e.target.value)}
                  disabled={!!detail.organization.is_whitelisted}
                />
              </div>

              <div className="border-t pt-4">
                <p className="font-medium text-slate-900 mb-2">Actieve modules</p>
                <ModuleBadgeList modules={detail.enabled_modules} />
              </div>

              <div className="border-t pt-4">
                <p className="font-medium text-slate-900 mb-2">Audit-log</p>
                <AuditLogList entries={detail.audit_log} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe mail versturen</DialogTitle>
          </DialogHeader>
          <form onSubmit={sendMail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mail-to">Aan</Label>
              <Input
                id="mail-to" type="email" required
                value={composeForm.to}
                onChange={(e) => setComposeForm((f) => ({ ...f, to: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mail-subject">Onderwerp</Label>
              <Input
                id="mail-subject" required
                value={composeForm.subject}
                onChange={(e) => setComposeForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mail-body">Bericht</Label>
              <Textarea
                id="mail-body" required rows={6}
                value={composeForm.body}
                onChange={(e) => setComposeForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mail-reply-to">Antwoord-adres (optioneel)</Label>
              <Input
                id="mail-reply-to" type="email" placeholder="standaard: jouw eigen e-mailadres"
                value={composeForm.reply_to}
                onChange={(e) => setComposeForm((f) => ({ ...f, reply_to: e.target.value }))}
              />
            </div>
            <Button type="submit" disabled={sendingMail}>{sendingMail ? 'Verzenden...' : 'Verzenden'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const OrganizationRow = ({ org, onManage }) => (
  <TableRow>
    <TableCell className="font-medium">{org.name}</TableCell>
    <TableCell>{PLAN_LABELS[org.plan] || org.plan}</TableCell>
    <TableCell>{STATUS_LABELS[org.subscription_status] || org.subscription_status}</TableCell>
    <TableCell>
      <Badge className={org.has_access ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
        {org.has_access ? 'Toegang' : 'Geblokkeerd'}
      </Badge>
    </TableCell>
    <TableCell>
      {org.is_whitelisted ? <Badge className="bg-amber-100 text-amber-700">Whitelisted</Badge> : '—'}
    </TableCell>
    <TableCell>
      <Button size="sm" variant="outline" onClick={() => onManage(org.id)}>Beheren</Button>
    </TableCell>
  </TableRow>
);

const ModuleBadgeList = ({ modules }) => (
  <div className="flex flex-wrap gap-2">
    {modules.map((m) => (
      <Badge key={m} variant="outline">{m}</Badge>
    ))}
  </div>
);

const AuditLogEntry = ({ entry }) => (
  <div className="p-2 rounded-lg bg-slate-50">
    <p className="font-medium">{entry.action}</p>
    <p className="text-slate-500">{entry.actor_email} — {new Date(entry.created_at).toLocaleString('nl-NL')}</p>
    {entry.reason && <p className="text-slate-500 italic">"{entry.reason}"</p>}
  </div>
);

const AuditLogList = ({ entries }) => (
  <div className="space-y-2 max-h-48 overflow-y-auto text-sm">
    {entries.length === 0 && <p className="text-slate-400">Nog geen wijzigingen.</p>}
    {entries.map((entry) => (
      <AuditLogEntry key={entry.id} entry={entry} />
    ))}
  </div>
);

export default SuperAdminPage;
